import { lazy } from 'react';
import {
  useFela,
  StyleProvider,
  styleConfig,
  FelaComponent,
  ThemeProvider,
  connect,
  getRenderer,
  createComponent,
} from '../Theme/utils';
import { theme, seinzTheme } from '../Theme';
import { withSuspense } from '../utils/withSuspense';
import { initI18next } from '../initI18n';
import useNotification from '../Compounds/InteractiveNotifications/useNotification';
import NotificationProvider from '../Compounds/InteractiveNotifications/NotificationProvider';
// ⚠️ `LayerNewContent` must be imported/exported without code splitting (lazy & suspense) because otherwise we have no access to it's internal props.
import { LayerNewContent } from '../Molecules/LayerNew';

import Loading from '../Molecules/Loading/Loading';
import Button from '../Atoms/Button/Button';
import Collapsible from '../Atoms/Collapsible/Collapsible';
import Heading from '../Atoms/Heading/Heading';
import HighlightedText from '../Atoms/HighlightedText/HighlightedText';
import View from '../Atoms/Layout/View/View';
import Element from '../Atoms/Layout/Element/Element';
import Link from '../Atoms/Link/Link';
import List from '../Molecules/List/List';
import ListItem from '../Molecules/List/ListItem';
import Picture from '../Molecules/Images/Picture';

initI18next();

const Banner = withSuspense(
  lazy(
    () => import(/* webpackChunkName: "Banner" */ '../Molecules/Banner/Banner'),
  ),
  null,
);

const Text = withSuspense(
  lazy(() => import(/* webpackChunkName: "Text" */ '../Atoms/Text/Text')),
  null,
);

const Input = withSuspense(
  lazy(() => import(/* webpackChunkName: "Input" */ '../Atoms/Input/Input')),
  null,
);

const InputField = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "InputField" */ '../Molecules/InputField/InputField'
      ),
  ),
  null,
);

const InputLabel = withSuspense(
  lazy(
    () =>
      import(/* webpackChunkName: "InputLabel" */ '../Atoms/InputLabel/Label'),
  ),
  null,
);

const InputWithIcon = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "InputWithIcon" */ '../Molecules/InputWithIcon/InputWithIcon'
      ),
  ),
  null,
);

const Box = withSuspense(
  lazy(() => import(/* webpackChunkName: "Box" */ '../Atoms/Layout/Box/Box')),
  null,
);

const Cell = withSuspense(
  lazy(
    () => import(/* webpackChunkName: "Cell" */ '../Atoms/Layout/Cell/Cell'),
  ),
  null,
);

const Spacer = withSuspense(
  lazy(
    () =>
      import(/* webpackChunkName: "Spacer" */ '../Atoms/Layout/Spacer/Spacer'),
  ),
  null,
);

const Separator = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "Separator" */ '../Atoms/Separator/Separator'
      ),
  ),
  null,
);

const Textarea = withSuspense(
  lazy(
    () =>
      import(/* webpackChunkName: "Textarea" */ '../Atoms/Textarea/Textarea'),
  ),
  null,
);

const TextareaField = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "TextareaField" */ '../Molecules/TextareaField/TextareaField'
      ),
  ),
  null,
);

const Form = withSuspense(
  lazy(() => import(/* webpackChunkName: "Form" */ '../Molecules/Forms/Form')),
  Loading,
);

const Notification = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "Notification" */ '../Molecules/Notification/Notification'
      ),
  ),
  Loading,
);

const NotificationContainer = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "Notification" */ '../Molecules/NotificationContainer/NotificationContainer'
      ),
  ),
  Loading,
);

const WhatsappShareButton = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "ShareButtons" */ '../Molecules/ShareButton/WhatsappShareButton'
      ),
  ),
  Loading,
);

const FacebookShareButton = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "ShareButtons" */ '../Molecules/ShareButton/FacebookShareButton'
      ),
  ),
  Loading,
);

const SearchForm = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "SearchForm" */ '../Molecules/SearchForm/SearchForm'
      ),
  ),
  Loading,
);

const SearchFormWithSuggestions = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "SearchFormWithSuggestions" */ '../Compounds/SearchSuggestions/SearchFormWithSuggestions'
      ),
  ),
  Loading,
);

const Switch = withSuspense(
  lazy(() => import(/* webpackChunkName: "Switch" */ '../Atoms/Switch/Switch')),
  null,
);

const Checkbox = withSuspense(
  lazy(
    () =>
      import(/* webpackChunkName: "Checkbox" */ '../Atoms/Checkbox/Checkbox'),
  ),
  null,
);

const CheckboxField = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "CheckboxField" */ '../Molecules/CheckboxField/CheckboxField'
      ),
  ),
  Loading,
);

const Dropdown = withSuspense(
  lazy(
    () =>
      import(/* webpackChunkName: "Dropdown" */ '../Atoms/Dropdown/Dropdown'),
  ),
  null,
);

const DropdownField = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "DropdownField" */ '../Molecules/DropdownField/DropdownField'
      ),
  ),
  Loading,
);

const RadioButton = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "RadioButton" */ '../Atoms/RadioButton/RadioButton'
      ),
  ),
  null,
);

const RadioButtonField = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "RadioButtonField" */ '../Molecules/RadioButtonField/RadioButtonField'
      ),
  ),
  Loading,
);

const RadioButtonFieldGroup = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "RadioButtonFieldGroup" */ '../Molecules/RadioButtonFieldGroup/RadioButtonFieldGroup'
      ),
  ),
  Loading,
);

const Layer = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "Layer" */ '../Molecules/LayerDeprecated/Layer'
      ),
  ),
  Loading,
);

const LayerNew = withSuspense(
  lazy(
    () =>
      import(/* webpackChunkName: "LayerNew" */ '../Molecules/LayerNew/Layer'),
  ),
  null,
);

const LayerNewHeader = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "LayerNew" */ '../Molecules/LayerNew/LayerHeader'
      ),
  ),
  Loading,
);

const LayerNewPadding = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "LayerNew" */ '../Molecules/LayerNew/LayerPadding'
      ),
  ),
  null,
);

const LayerNewFooter = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "LayerNew" */ '../Molecules/LayerNew/LayerFooter'
      ),
  ),
  null,
);

const BaseSlider = withSuspense(
  lazy(
    () =>
      import(/* webpackChunkName: "Slider" */ '../Molecules/Slider/BaseSlider'),
  ),
  Loading,
);

const TileSlider = withSuspense(
  lazy(
    () =>
      import(/* webpackChunkName: "Slider" */ '../Molecules/Slider/TileSlider'),
  ),
  Loading,
);

const FullWidthSlider = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "Slider" */ '../Molecules/Slider/FullWidthSlider'
      ),
  ),
  Loading,
);

const DateInput = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "DateInput" */ '../Molecules/DateInput/DateInput'
      ),
  ),
  Loading,
);

const DateInputField = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "DateInputField" */ '../Molecules/DateInputField/DateInputField'
      ),
  ),
  Loading,
);

const RangeSlider = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "RangeSlider" */ '../Molecules/RangeSlider/RangeSlider'
      ),
  ),
  null,
);

const RangeSliderWithFields = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "RangeSliderWithFields" */ '../Compounds/RangeSliderWithFields/RangeSliderWithFields'
      ),
  ),
  null,
);

const Tabs = withSuspense(
  lazy(() => import(/* webpackChunkName: "Tabs" */ '../Molecules/Tabs/Tabs')),
  Loading,
);

const TabList = withSuspense(
  lazy(
    () => import(/* webpackChunkName: "Tabs" */ '../Molecules/Tabs/TabList'),
  ),
  Loading,
);

const Tab = withSuspense(
  lazy(() => import(/* webpackChunkName: "Tabs" */ '../Molecules/Tabs/Tab')),
  Loading,
);

const TabPanel = withSuspense(
  lazy(
    () => import(/* webpackChunkName: "Tabs" */ '../Molecules/Tabs/TabPanel'),
  ),
  Loading,
);

const Tooltip = withSuspense(
  lazy(
    () =>
      import(/* webpackChunkName: "Tooltip" */ '../Molecules/Tooltip/Tooltip'),
  ),
  Loading,
);

const TooltipPortal = withSuspense(
  lazy(
    () =>
      import(
        /* webpackChunkName: "Tooltip" */ '../Molecules/Tooltip/TooltipPortal'
      ),
  ),
  Loading,
);

export {
  Banner,
  BaseSlider,
  Box,
  Button,
  Cell,
  Checkbox,
  CheckboxField,
  Collapsible,
  Dropdown,
  DropdownField,
  Element,
  FelaComponent,
  Form,
  FullWidthSlider,
  Heading,
  HighlightedText,
  Input,
  InputField,
  InputLabel,
  InputWithIcon,
  Layer,
  LayerNew,
  LayerNewHeader,
  LayerNewContent,
  LayerNewPadding,
  LayerNewFooter,
  Link,
  List,
  ListItem,
  Loading,
  Notification,
  NotificationContainer,
  NotificationProvider,
  useNotification,
  Picture,
  RadioButton as Radio, // DEPRECATED!
  RadioButton,
  RadioButtonField,
  RadioButtonFieldGroup,
  RangeSlider,
  RangeSliderWithFields,
  FacebookShareButton,
  WhatsappShareButton,
  getRenderer,
  SearchForm,
  SearchFormWithSuggestions,
  Separator,
  Spacer,
  styleConfig,
  StyleProvider,
  Switch,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Text,
  Textarea,
  TextareaField,
  TileSlider,
  theme,
  ThemeProvider,
  Tooltip,
  TooltipPortal,
  seinzTheme,
  createComponent as createFelaComponent,
  connect as connectFela,
  useFela,
  DateInput,
  DateInputField,
  View,
};

export * from './dm-icons';
